import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSquare, AlertCircle, CheckCircle2, Star } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { api, ApiError } from '../../lib/api';

interface ViewResult {
  person: { first_name: string; full_name: string };
  already_submitted: boolean;
}

/**
 * Public feedback form, reached via a magic link emailed to the
 * attendee after the day. No login. Single-use; once submitted, the
 * link is burned.
 */
export function FeedbackForm() {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<ViewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rating, setRating] = useState<number>(5);
  const [worked, setWorked] = useState('');
  const [improve, setImprove] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) return;
    (async () => {
      try {
        const r = await api<ViewResult>(`/api/feedback/${token}`);
        if (cancelled) return;
        setView(r);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('This link is invalid or has expired.');
        } else {
          setError('Could not load the form. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await api(`/api/feedback/${token}`, {
        method: 'POST',
        body: JSON.stringify({
          rating,
          ...(worked.trim() ? { what_worked: worked.trim() } : {}),
          ...(improve.trim() ? { what_to_improve: improve.trim() } : {}),
        }),
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (error && !view) {
    return (
      <main className="min-h-screen [background:var(--hero-gradient),rgb(var(--bg))] p-6 text-fg">
        <div className="mx-auto mt-12 max-w-md rounded-2xl border border-line/10 bg-surface p-6 shadow-lg sm:p-8">
          <div className="flex items-center gap-2 text-warning-soft-fg">
            <AlertCircle className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Sorry</h1>
          </div>
          <p className="mt-3 text-sm text-fg-muted">{error}</p>
        </div>
      </main>
    );
  }
  if (!view) return null;

  if (done) {
    return (
      <main className="min-h-screen [background:var(--hero-gradient),rgb(var(--bg))] p-6 text-fg">
        <div className="mx-auto mt-12 max-w-md rounded-2xl border border-success/30 bg-surface p-6 shadow-lg sm:p-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-success text-success-fg shadow-md">
            <CheckCircle2 className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight text-success-soft-fg">
            Thanks!
          </h1>
          <p className="mt-2 text-sm text-fg-muted">
            Your feedback has been recorded.
          </p>
        </div>
      </main>
    );
  }

  if (view.already_submitted) {
    return (
      <main className="min-h-screen [background:var(--hero-gradient),rgb(var(--bg))] p-6 text-fg">
        <div className="mx-auto mt-12 max-w-md rounded-2xl border border-line/10 bg-surface p-6 shadow-lg sm:p-8">
          <h1 className="text-xl font-semibold">Already submitted</h1>
          <p className="mt-2 text-sm text-fg-muted">
            We've already received feedback for{' '}
            <strong>{view.person.full_name}</strong>. Thanks again.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen [background:var(--hero-gradient),rgb(var(--bg))] p-6 text-fg">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-fg-on-accent shadow-md">
            <MessageSquare className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">How was the day?</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Hi {view.person.first_name}, two minutes max. Anything you tell
            us helps the next mobilisation run smoother.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-5 rounded-2xl border border-line/10 bg-surface p-6 shadow-lg sm:p-8"
        >
          <fieldset>
            <legend className="text-sm font-semibold text-fg-muted">
              Overall rating
            </legend>
            <div className="mt-2 grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRating(r)}
                  aria-label={`${r} out of 5`}
                  aria-pressed={rating === r}
                  className={`flex h-12 flex-col items-center justify-center rounded-md border text-sm font-semibold transition-all ${
                    rating === r
                      ? 'border-slate-900 bg-accent text-fg-on-accent shadow-sm'
                      : 'border-line-strong/20 bg-surface text-fg-muted hover:border-accent/40 hover:bg-surface-2'
                  }`}
                >
                  <Star
                    className="h-4 w-4"
                    fill={rating >= r ? 'currentColor' : 'transparent'}
                    strokeWidth={1.5}
                  />
                  <span className="text-xs">{r}</span>
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-fg-muted">1 = poor, 5 = excellent</p>
          </fieldset>

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">What worked well?</span>
            <textarea
              rows={3}
              value={worked}
              onChange={(e) => setWorked(e.target.value)}
              className="mt-1.5 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">What could we improve?</span>
            <textarea
              rows={3}
              value={improve}
              onChange={(e) => setImprove(e.target.value)}
              className="mt-1.5 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
          </label>

          {error ? (
            <div className="flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={saving}
          >
            Submit feedback
          </Button>
        </form>
      </div>
    </main>
  );
}
